import { Box, Grid, Typography } from "@material-ui/core";
import { GenericSelect, Option } from "~/components/select/genericSelect";
import React, { memo, useEffect, useState } from "react";
import {
  useGetReportRunsQuery,
  useReportTypesQuery,
  useScheduledReportsQuery,
} from "~/store/mystore/amc.redux";

import AMCReportCommentary from "~/modules/amc/amcReportCommentary";
import AMCReportVisualization from "~/modules/amc/amcReportVisualization";
import { DEFAULT_CURRENCY } from "~/store/persistentAppSettings.redux";
import { GenericReportTable } from "~/modules/amc/genericReportTable";
import { GridWithErrorBoundary } from "~/modules/errorBoundary/errorBoundary";
import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import PageBlock from "~/components/containers/sideNavPageBlock";
import Panel from "~/components/panel/panel";
import { PrunedAMCReportType } from "~/typedef/amc/reportType";
import { PrunedAMCScheduledReport } from "~/typedef/amc/scheduledReport";
import { PrunedAMCScheduledReportRun } from "~/typedef/amc/scheduledReportRun";
import { get } from "lodash";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

const REPORT_TABLE_PAGE_SIZE = 20;

const formatRunDate = (startDate: string, endDate: string) => {
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);
  return `${startDateMoment.format("MMM D, YYYY")} – ${endDateMoment.format(
    "MMM D, YYYY"
  )}`;
};

const ViewAmcReport = memo(function ViewAmcReport() {
  const { t } = useTranslation();
  const location = useLocation();

  const store = useTypedSelector((state) =>
    get(state, "persistentAppSettings.setting.data.currentStore")
  );

  const currentCurrency = useTypedSelector(
    (state) =>
      get(state, "persistentAppSettings.setting.data.currentCurrency") ||
      DEFAULT_CURRENCY
  );

  const [selectedReportType, setSelectedReportType] = useState<
    PrunedAMCReportType | undefined
  >();
  const [selectedScheduledReport, setSelectedScheduledReport] = useState<
    PrunedAMCScheduledReport | undefined
  >();
  const [currentScheduledReportRun, setCurrentScheduledReportRun] = useState<
    PrunedAMCScheduledReportRun | undefined
  >();
  const [priorScheduledReportRun, setPriorScheduledReportRun] = useState<
    PrunedAMCScheduledReportRun | undefined
  >();

  const { amcReportTypes, reportTypesLoading } = useReportTypesQuery(
    {},
    {
      selectFromResult: ({ data, isFetching }) => {
        return {
          amcReportTypes: data?.reportTypes ?? [],
          reportTypesLoading: isFetching,
        };
      },
    }
  );

  const { scheduledReports } = useScheduledReportsQuery(
    {
      mid: store?.merchantId || "",
      queryType: selectedReportType?._id || "",
      paginationParams: {
        pageIndex: 0,
        pageSize: 100,
        sortKey: "createdAt",
        sortOrder: "desc",
      },
    },
    {
      skip: !selectedReportType || !store,
      selectFromResult: ({ data }) => ({
        scheduledReports: data?.scheduledReports ?? [],
      }),
    }
  );

  const { scheduledReportRuns } = useGetReportRunsQuery(
    {
      mid: store?.merchantId || "",
      scheduledReportId: selectedScheduledReport?._id || "",
    },
    {
      skip: !selectedScheduledReport || !store,
      selectFromResult: ({ data }) => ({
        scheduledReportRuns: data?.reportRuns ?? [],
      }),
    }
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryType = params.get("scheduleReportType");

    if (queryType && amcReportTypes.length > 0) {
      const reportType = amcReportTypes.find((type) => type._id === queryType);
      if (reportType) {
        setSelectedReportType(reportType);
      }
    }
  }, [location.search, amcReportTypes]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scheduledReportId = params.get("scheduledReport");

    if (scheduledReports.length > 0 && scheduledReportId) {
      const scheduledReport = scheduledReports.find(
        (report) => report._id === scheduledReportId
      );
      if (scheduledReport) {
        setSelectedScheduledReport(scheduledReport);
      }
    }
  }, [scheduledReports, location.search]);

  if (!store || reportTypesLoading) {
    return (
      <PageBlock>
        <LoadingIndicator />
      </PageBlock>
    );
  }

  return (
    <PageBlock>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <GenericSelect
            id="amc-report-type-select"
            title={t("amc.selectReportType.label")}
            currentOption={
              selectedReportType
                ? {
                    name: selectedReportType._id,
                    label: t(`amc.${selectedReportType.name}.label`),
                  }
                : undefined
            }
            setOption={(option) => {
              const reportType = amcReportTypes.find(
                (item) => item._id === option.name
              );
              if (reportType) {
                setSelectedReportType(reportType);
                setSelectedScheduledReport(undefined);
                setCurrentScheduledReportRun(undefined);
                setPriorScheduledReportRun(undefined);
              }
            }}
            options={amcReportTypes.map((reportType) => ({
              name: reportType._id,
              label: t(`amc.${reportType.name}.label`),
            }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GenericSelect
            id="amc-scheduled-report-select"
            title={t("amc.selectScheduledReport.label")}
            currentOption={
              selectedScheduledReport
                ? {
                    name: selectedScheduledReport._id,
                    label: selectedScheduledReport.reportName,
                  }
                : undefined
            }
            setOption={(option) => {
              const scheduledReport = scheduledReports.find(
                (item) => item._id === option.name
              );
              if (scheduledReport) {
                setSelectedScheduledReport(scheduledReport);
                setCurrentScheduledReportRun(undefined);
                setPriorScheduledReportRun(undefined);
              }
            }}
            options={scheduledReports.map((report) => ({
              name: report._id,
              label: report.reportName,
            }))}
            disabled={!selectedReportType}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GenericSelect
            id="amc-scheduled-report-run-current-select"
            title={t("amc.selectScheduledReportRunCurrent.label")}
            currentOption={
              currentScheduledReportRun
                ? {
                    name: currentScheduledReportRun._id,
                    label: formatRunDate(
                      currentScheduledReportRun.workflowExecution
                        .timeWindowStart,
                      currentScheduledReportRun.workflowExecution.timeWindowEnd
                    ),
                  }
                : undefined
            }
            setOption={(option) => {
              const scheduledReportRun = scheduledReportRuns.find(
                (item) => item._id === option.name
              );
              scheduledReportRun &&
                setCurrentScheduledReportRun(scheduledReportRun);
            }}
            options={scheduledReportRuns.map((report) => ({
              name: report._id,
              label: formatRunDate(
                report.workflowExecution.timeWindowStart,
                report.workflowExecution.timeWindowEnd
              ),
            }))}
            disabled={!selectedScheduledReport}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GenericSelect
            id="amc-scheduled-report-run-prior-select"
            title={t("amc.selectScheduledReportRunPrior.label")}
            currentOption={
              priorScheduledReportRun
                ? {
                    name: priorScheduledReportRun._id,
                    label: formatRunDate(
                      priorScheduledReportRun.workflowExecution.timeWindowStart,
                      priorScheduledReportRun.workflowExecution.timeWindowEnd
                    ),
                  }
                : undefined
            }
            setOption={(option) => {
              const scheduledReportRun = scheduledReportRuns.find(
                (item) => item._id === option.name
              );
              scheduledReportRun &&
                setPriorScheduledReportRun(scheduledReportRun);
            }}
            options={(scheduledReportRuns || []).map((report) => ({
              name: report._id,
              label: formatRunDate(
                report.workflowExecution.timeWindowStart,
                report.workflowExecution.timeWindowEnd
              ),
            }))}
            disabled={!currentScheduledReportRun}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            {selectedScheduledReport &&
              selectedScheduledReport.description.trim() !== "" && (
                <GridWithErrorBoundary item xs={12}>
                  <Panel
                    id="amc-report-description-panel"
                    title={t(`amcWidget.reportDescriptionLabel`)}
                    content={
                      <Box p={2}>
                        <Typography variant="body1" color="textSecondary">
                          {selectedScheduledReport.description}
                        </Typography>
                      </Box>
                    }
                  />
                </GridWithErrorBoundary>
              )}
            {selectedReportType && currentScheduledReportRun && (
              <>
                {selectedReportType.visualizations && (
                  <AMCReportVisualization
                    store={store}
                    reportTypeName={selectedReportType.name}
                    currentReportRunId={currentScheduledReportRun._id}
                    visualizations={selectedReportType.visualizations}
                    currentCurrency={currentCurrency}
                  />
                )}
                {selectedReportType.tabular && (
                  <GridWithErrorBoundary item xs={12}>
                    <GenericReportTable
                      store={store}
                      reportType={selectedReportType}
                      currentReportRunId={currentScheduledReportRun._id}
                      priorReportRunId={priorScheduledReportRun?._id}
                      currentCurrency={currentCurrency}
                      pageSize={REPORT_TABLE_PAGE_SIZE}
                    />
                  </GridWithErrorBoundary>
                )}
                {selectedReportType.hasCommentaryPrompt && (
                  <GridWithErrorBoundary item xs={12}>
                    <AMCReportCommentary
                      mid={store.merchantId}
                      reportRunId={currentScheduledReportRun._id}
                    />
                  </GridWithErrorBoundary>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </PageBlock>
  );
});

export default ViewAmcReport;
